import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import httpService from '../../services/httpService';
import { ApiEndpoints, Constants } from '../../constants/appConstants';
import { useExcelImportPopup } from '../Common/useExcelImportPopup';
import { useDataManagementFileUpload } from '../Common/useDataManagementFileUpload';
import { ImportTemplates } from '../../constants/appConstants';
import { createDialogOverlayStyles } from '../../utils/safeCssInjection';
import './TiersComponent.scss';
import { Toast } from 'primereact/toast';
import DataManagementForm, { DataManagementOption } from '../DataManagement/DataManagementForm';
import { dataManagementFormConfig, getFormValidationErrors, normalizeFieldValues } from '../DataManagement/dataManagementFormConfig';
import DataManagementLoader from '../DataManagement/DataManagementLoader';

const fetchTiers = () => {
  const url =  `${ApiEndpoints.GetTiersWithInactive}`;
  return httpService.get(url);
};

interface TiersComponentProps {
  providerTypes: { label: string; value: string }[];
}

const TiersComponent: React.FC<TiersComponentProps> = ({ providerTypes }) => {
  // Inject custom style for dialog overlay to ensure lighter background
  React.useEffect(() => {
    const cleanup = createDialogOverlayStyles('tiers-component-styles');
    return cleanup;
  }, []);

  const [tiers, setTiers] = useState<any[]>([]);
  const toast = React.useRef<Toast>(null);
  const { uploadFile } = useDataManagementFileUpload();

  const handleExcelUpload = async (file: File) => {
    setLoading(true);
    try {
      const fileKey = await uploadFile(file, Constants.DATA_MANAGEMENT_MODULES.TIERS);

      await httpService.post(ApiEndpoints.BulkUploadDataManagement, {
        fileKey,
        fileName: file.name,
        module: Constants.DATA_MANAGEMENT_MODULES.TIERS,
      });

      const response = await fetchTiers();
      setTiers(response.data as any[]);
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Tiers uploaded successfully', life: 3000 });
    } catch (err: any) {
      let msg = err?.response?.data?.message || err?.message || "Bulk upload failed";
      toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const { showImport, ImportPopup } = useExcelImportPopup({
    templateUrl: ImportTemplates.tiers,
    onUpload: handleExcelUpload,
    title: 'Import Tiers from Excel',
  });

  const [providerTypeFilter, setProviderTypeFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [selectedTier, setSelectedTier] = useState<any>(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [tierToDelete, setTierToDelete] = useState<any>(null);

  useEffect(() => {
    setLoading(true);
    fetchTiers()
      .then((response: any) => {
        setTiers(response.data);
        setLoading(false);
      })
      .catch((err: any) => {
        setError(`Failed to fetch tiers: ${err.message}`);
        setLoading(false);
      });
  }, []);

  // Get unique provider types for dropdown (filter)
  const providerTypeOptions = providerTypes && providerTypes.length > 0
    ? providerTypes
    : Array.from(new Set(tiers.map(t => t.providerType).filter(Boolean))).map(pt => ({ label: pt, value: pt }));

  // Filter tiers by provider type and search term
  let filteredTiers = providerTypeFilter ? tiers.filter(t => t.providerType === providerTypeFilter) : tiers;
  if (searchTerm.trim()) {
    filteredTiers = filteredTiers.filter(t =>
      t.tierName && t.tierName.toLowerCase().includes(searchTerm.trim().toLowerCase())
    );
  }

  return (
    <div style={{ marginTop: 24, position: 'relative', minHeight: 220 }} aria-busy={loading}>
      <DataManagementLoader visible={loading} label="Loading tiers" />
      <Toast ref={toast} position="top-right" style={{ zIndex: 9999 }} />
      {error && <div style={{ color: 'red' }}>{error}</div>}
      {ImportPopup}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, gap: 16 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button label="Add New Tier" icon="pi pi-plus" onClick={() => {
            setSelectedTier({ tierId: 0, tierName: '', additionalTierDetails: '', isActive: true, tierIdentifier: '', isActualTier: false, tierCode: '', sequence: 0, providerType: '', tierLevel: null, parentTierId: null });
            console.log(selectedTier)
            setIsAddMode(true);
            setEditDialogVisible(true);
          }} />
          <Button label="Import" icon="pi pi-upload" className="p-button-secondary" onClick={showImport} style={{ marginLeft: 8 }} />
        </div>
        <div className="tier-search-anim">
          <input
            type="text"
            placeholder="Search by Tier Name..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <span className="pi pi-search" />
        </div>
        <div className="provider-type-dropdown">
          <select
            value={providerTypeFilter}
            onChange={e => setProviderTypeFilter(e.target.value)}
          >
            <option value="">All Provider Types</option>
            {providerTypeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <span className="pi pi-angle-down" />
        </div>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
        <thead>
          <tr style={{ background: '#f0f4f8' }}>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 100 }}>#</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 100 }}>Tier Name</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 100 }}>Tier Code</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 100 }}>Tier Identifier</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 100 }}>Provider Type</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 100 }}>Additional Details</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 100 }}>Sequence</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 100 }}>Tier Level</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 100 }}>Is Active</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 100 }}>Parent Tier Name</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 100 }}>Is Actual Tier</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 140 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredTiers.length === 0 ? (
            <tr><td colSpan={12} style={{ textAlign: 'center', padding: 16 }}>No tiers found.</td></tr>
          ) : (
            filteredTiers.map((tier: any, idx: number) => (
              <tr
                key={tier.tierId || idx}
                style={{ background: idx % 2 === 0 ? '#fff' : '#f6f8fa' }}
                // style={{
                //   background: idx % 2 === 0 ? '#fff' : '#f6f8fa',
                //   //borderBottom: '2px solid #b0b6be',
                //   //borderTop: '2px solid #b0b6be'
                // }}
              >
                <td style={{ padding: 8, border: '1px solid #eee' }}>{idx + 1}</td>
                <td style={{ padding: 8, border: '1px solid #eee' }}>{tier.tierName || '-'}</td>
                <td style={{ padding: 8, border: '1px solid #eee' }}>{tier.tierCode || '-'}</td>
                <td style={{ padding: 8, border: '1px solid #eee' }}>{tier.tierIdentifier || '-'}</td>
                <td style={{ padding: 8, border: '1px solid #eee' }}>{tier.providerType || '-'}</td>
                <td style={{ padding: 8, border: '1px solid #eee' }}>{tier.additionalTierDetails || '-'}</td>
                <td style={{ padding: 8, border: '1px solid #eee' }}>{tier.sequence ?? '-'}</td>
                <td style={{ padding: 8, border: '1px solid #eee' }}>{tier.tierLevel ?? '-'}</td>
                <td style={{ padding: 8, border: '1px solid #eee' }}>{tier.isActive ? 'Yes' : 'No'}</td>
                <td style={{ padding: 8, border: '1px solid #eee' }}>{tier.parentTierName || '-'}</td>
                <td style={{ padding: 8, border: '1px solid #eee' }}>{tier.isActualTier ? 'Yes' : 'No'}</td>
                <td style={{ padding: 8, border: '1px solid #eee' }}>
                  <Button icon="pi pi-pencil" className="p-button-text" style={{ marginRight: 8 }} onClick={() => { setSelectedTier({ ...tier }); setIsAddMode(false); setEditDialogVisible(true); }} />
                  <Button icon="pi pi-trash" className="p-button-text p-button-danger" onClick={() => { setTierToDelete(tier); setDeleteDialogVisible(true); }} />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <div style={{ marginTop: 16, textAlign: 'right', color: '#888' }}>
        {/* Pagination placeholder */}
      </div>
      <Dialog
        header={isAddMode ? "Add Tier" : `Edit Tier${selectedTier && selectedTier.tierName ? ` - ${selectedTier.tierName}` : ''}`}
        visible={editDialogVisible}
        style={{ width: '600px', maxWidth: '90vw' }}
        onHide={() => { setEditDialogVisible(false); setIsAddMode(false); }}
        footer={
          <div>
            <Button label="Cancel" onClick={() => { setEditDialogVisible(false); setIsAddMode(false); }} className="p-button-text" />
            <Button label="Save" onClick={async () => {
              const validationErrors = getFormValidationErrors(dataManagementFormConfig.tiers, selectedTier);
              if (validationErrors.length > 0) {
                toast.current?.show({ severity: 'warn', summary: 'Fix the following', detail: validationErrors.join(', '), life: 4000 });
                return;
              }
              const payload = normalizeFieldValues(dataManagementFormConfig.tiers, selectedTier);
              if (isAddMode) {
                try {
                  setLoading(true);
                  await httpService.post(ApiEndpoints.AddTier, payload);
                  const response = await fetchTiers();
                  setTiers(response.data as any[]);
                  setEditDialogVisible(false);
                  setIsAddMode(false);
                  // Success toast
                  toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Tier added successfully', life: 3000 });
                } catch (err: any) {
                  let msg = err?.response?.data?.message || err?.message || "Unknown error";
                  toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
                  setTimeout(() => {
                    setEditDialogVisible(false);
                    setIsAddMode(false);
                  }, 100);
                } finally {
                  setLoading(false);
                }
              } else if (selectedTier) {
                try {
                  setLoading(true);
                  await httpService.put(`${ApiEndpoints.UpdateTier}/${selectedTier.tierId}`, payload);
                  const response = await fetchTiers();
                  setTiers(response.data as any[]);
                  setEditDialogVisible(false);
                  setIsAddMode(false);
                  // Success toast
                  toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Tier updated successfully', life: 3000 });
                } catch (err: any) {
                  let msg = err?.response?.data?.message || err?.message || "Unknown error";
                  toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
                  setTimeout(() => {
                    setEditDialogVisible(false);
                    setIsAddMode(false);
                  }, 100);
                } finally {
                  setLoading(false);
                }
              }
            }} loading={loading} disabled={loading} autoFocus />
          </div>
        }
      >
        <TierEditForm
          editTier={selectedTier}
          setEditTier={setSelectedTier}
          providerTypes={providerTypes}
          tiers={tiers}
          isEditing={!isAddMode}
        />
      </Dialog>
      <Dialog
        header="Confirm Delete"
        visible={deleteDialogVisible}
        style={{ width: '350px' }}
        onHide={() => setDeleteDialogVisible(false)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button label="Yes" autoFocus={false} onClick={() => {
              setTiers(tiers.filter(t => t.tierId !== (tierToDelete && tierToDelete.tierId)));
              setDeleteDialogVisible(false);
            }} className="p-button-danger" />
            <Button label="No" autoFocus={true} onClick={() => setDeleteDialogVisible(false)} className="p-button-secondary" />
          </div>
        }
      >
        <div style={{ fontSize: 16, margin: '16px 0' }}>
          Are you sure you want to delete this record{tierToDelete && tierToDelete.tierName ? `: ${tierToDelete.tierName}` : ''}?
        </div>
      </Dialog>
    </div>
  );
}

interface TierEditFormProps {
  editTier: any;
  setEditTier: (tier: any) => void;
  providerTypes: { label: string; value: string }[];
  tiers: any[];
  isEditing: boolean;
}

const TierEditForm: React.FC<TierEditFormProps> = ({ editTier, setEditTier, providerTypes, tiers, isEditing }) => {
  const parentTierOptions: DataManagementOption[] = editTier?.providerType
    ? tiers
      .filter(tier => tier.providerType === editTier.providerType && tier.tierId !== editTier.tierId)
      .map(tier => ({ label: tier.tierName, value: tier.tierId }))
    : [];

  return (
    <DataManagementForm
      fields={dataManagementFormConfig.tiers}
      values={editTier}
      onChange={setEditTier}
      options={{ providerType: providerTypes, parentTierId: parentTierOptions }}
      isEditing={isEditing}
    />
  );
};


export default TiersComponent;